/**
 * Maps transcript backup-stage keys to the role that produced that snapshot.
 *
 * Stage backups are written on entry to the *next* stage:
 *   speaker_final  – First Review editor's work (snapshot taken entering QA_Review)
 *   fd_final       – QA reviewer's work (snapshot taken entering Compliance)
 *   human_final    – Admin/Compliance work (snapshot taken entering Publication)
 *   current        – whoever is editing the live transcript (from workflow_status)
 */

export const DIFF_VERSION_CHAIN = [
  "ai_transcript",
  "speaker_final",
  "fd_final",
  "human_final",
  "current",
];

const STATUS_ORDER = {
  Not_Started: 0,
  First_Review: 1,
  QA_Review: 2,
  Compliance: 3,
  Publication: 4,
};

/**
 * Snapshots that should exist for this workflow, inferred from review stage.
 * Mirrors the speaker-tag comparison page so we don't request backups that
 * haven't been written yet.
 */
export function availableDiffVersionKeys(workflow) {
  const order = STATUS_ORDER[workflow?.workflow_status];
  const keys = [];

  if (workflow?.upload_type !== "AI") keys.push("ai_transcript");
  if (order === undefined || order >= STATUS_ORDER.QA_Review) keys.push("speaker_final");
  if (order === undefined || order >= STATUS_ORDER.Compliance) keys.push("fd_final");
  if (order === undefined || order >= STATUS_ORDER.Publication) keys.push("human_final");
  keys.push("current");

  return keys;
}

const BACKUP_FLAG = {
  ai_transcript: "is_ai",
  speaker_final: "is_speaker_final",
  fd_final: "is_fd_final",
  human_final: "is_human_final",
};

/**
 * Stage keys the server actually holds a snapshot for, from GET /transcript/<id>/backup_versions.
 * Preferred over `availableDiffVersionKeys`, which only infers them from the review stage.
 */
export function versionKeysFromBackups(versions) {
  const present = new Set(["current"]);

  (versions || []).forEach((version) => {
    Object.entries(BACKUP_FLAG).forEach(([key, flag]) => {
      if (version?.[flag]) present.add(key);
    });
  });

  return DIFF_VERSION_CHAIN.filter((key) => present.has(key));
}

export const DIFF_ACTORS = {
  editor: {
    id: "editor",
    label: "Editor",
    color: "#2971fa",
    darkColor: "#78A6FF",
  },
  qa: {
    id: "qa",
    label: "QA",
    color: "#12B76A",
    darkColor: "#32D583",
  },
  admin: {
    id: "admin",
    label: "Admin",
    color: "#D92D20",
    darkColor: "#FDA29B",
  },
  unknown: {
    id: "unknown",
    label: "Unknown",
    color: "#4B5565",
    darkColor: "#D0D5DD",
  },
};

export const DIFF_VERSION_LABELS = {
  ai_transcript: "Initial",
  speaker_final: "Speaker Verified",
  fd_final: "First Draft Final",
  human_final: "Human Final",
  current: "Current",
};

const VERSION_ACTOR = {
  speaker_final: "editor",
  fd_final: "qa",
  human_final: "admin",
};

/**
 * Role responsible for edits that first appear in `versionKey`.
 * `current` is whoever owns the live workflow stage.
 */
export function actorForVersion(versionKey, workflowStatus) {
  if (versionKey === "current") {
    if (workflowStatus === "First_Review" || workflowStatus === "Not_Started") {
      return "editor";
    }
    if (workflowStatus === "QA_Review") {
      return "qa";
    }
    return "admin";
  }

  return VERSION_ACTOR[versionKey] || null;
}

/**
 * Stage keys strictly after `sourceKey` up to and including `targetKey`.
 * Each of these is a blame hop: changes that first appear there belong to
 * that hop's actor.
 */
export function getDiffHopKeys(sourceKey, targetKey) {
  const sourceIdx = DIFF_VERSION_CHAIN.indexOf(sourceKey);
  const targetIdx = DIFF_VERSION_CHAIN.indexOf(targetKey);

  if (sourceIdx === -1 || targetIdx === -1 || targetIdx <= sourceIdx) {
    return targetKey ? [targetKey] : [];
  }

  return DIFF_VERSION_CHAIN.slice(sourceIdx + 1, targetIdx + 1);
}

/**
 * Intermediate keys that must be fetched to attribute source→target changes.
 * Excludes source and target themselves (those are already loaded).
 */
export function getIntermediateVersionKeys(sourceKey, targetKey, availableKeys = DIFF_VERSION_CHAIN) {
  const available = new Set(availableKeys);
  return getDiffHopKeys(sourceKey, targetKey).filter(
    (key) => key !== sourceKey && key !== targetKey && available.has(key)
  );
}

/**
 * Build [{ actor, transcript }] hops from already-fetched version snapshots.
 * Missing snapshots are skipped so comparison still works, with those edits
 * attributed to the next hop that did load.
 */
export function buildDiffHops(sourceKey, targetKey, transcriptSources, workflowStatus) {
  const hopKeys = getDiffHopKeys(sourceKey, targetKey);

  return hopKeys
    .map((key) => {
      const transcript = transcriptSources?.[key];
      const actor = actorForVersion(key, workflowStatus);

      if (!actor || !Array.isArray(transcript) || transcript.length === 0) {
        return null;
      }

      return { key, actor, transcript };
    })
    .filter(Boolean);
}

/**
 * Hops for a source→target compare. Intermediate snapshots come from
 * `transcriptSources`; the last hop is always the live target array so
 * word indices line up with `buildDiffView`.
 */
export function hopsForCompare(
  sourceKey,
  targetKey,
  transcriptSources,
  workflowStatus,
  targetTranscript
) {
  const hops = buildDiffHops(sourceKey, targetKey, transcriptSources, workflowStatus)
    .filter((hop) => hop.key !== targetKey);
  const actor = actorForVersion(targetKey, workflowStatus);

  if (actor && Array.isArray(targetTranscript) && targetTranscript.length > 0) {
    hops.push({ key: targetKey, actor, transcript: targetTranscript });
  }

  return hops;
}

export function uniqueActors(hops) {
  const seen = new Set();
  const actors = [];

  (hops || []).forEach((hop) => {
    if (hop?.actor && DIFF_ACTORS[hop.actor] && !seen.has(hop.actor)) {
      seen.add(hop.actor);
      actors.push(DIFF_ACTORS[hop.actor]);
    }
  });

  return actors;
}

/**
 * Required compare sides (source, and target when it is not the live transcript)
 * that the backup-versions list does not actually hold. `current` is never required
 * as a snapshot. Used to error instead of fetching a missing stage, which the
 * transcript endpoint would silently replace with the live transcript.
 */
export function missingRequiredVersionKeys(sourceKey, targetKey, availableKeys) {
  const available = new Set(availableKeys || []);

  return [sourceKey, targetKey].filter(
    (key) => key && key !== "current" && !available.has(key)
  );
}

export function missingSnapshotMessage(versionKey) {
  const label = DIFF_VERSION_LABELS[versionKey] || versionKey;

  return `The ${label} snapshot was not captured for this file`;
}
